import {
  buildDependencyRows,
  collectLineageDependents,
  compareLineageNodes,
  DEPENDENCY_EXPORT_HEADERS,
  type LineageDependent,
  type LineageExportNode,
  type LineageExportRef,
  lineageRefKey,
} from "src/components/lineage/lineage-export";
import type { LineageEdge } from "src/graphql";

export const NODES_SHEET_NAME = "Nodes";
export const EDGES_SHEET_NAME = "Edges";

const NODE_HEADERS = [
  "Resource ID",
  "Resource Type",
  "Resource Name",
  "Resource URL",
  "Exists",
];

const MODEL_KIND_SHEET_NAMES: Record<string, string> = {
  parent: "Parent Model",
  related: "Related Model",
  event: "Event Model",
  realtimeEvent: "Realtime Event Model",
  retl: "Reverse ETL Model",
};

const EDGE_HEADERS = [
  "Edge Type",
  "From Resource ID",
  "From Resource Type",
  "From Resource Name",
  "From Resource URL",
  "To Resource ID",
  "To Resource Type",
  "To Resource Name",
  "To Resource URL",
];

export type ExportNode = LineageExportNode;

export type ExportEdge = Pick<
  LineageEdge,
  "edgeType" | "fromNodeId" | "toNodeId"
>;

export type LineageExportSheet = {
  name: string;
  rows: string[][];
};

export type LineageExportInput = {
  nodes: ExportNode[];
  edges: ExportEdge[];
  /** Absolute link to a resource, or an empty string when nothing addresses it. */
  resourceUrl: (node: ExportNode) => string;
};

/**
 * Direct dependents of each resource, keyed by lineageRefKey.
 */
function buildDependentsIndex(
  nodes: ExportNode[],
  edges: ExportEdge[],
): Map<string, ExportNode[]> {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const dependentIdsByNodeId = new Map<string, Set<string>>();
  for (const edge of edges) {
    const dependents = dependentIdsByNodeId.get(edge.toNodeId);
    if (dependents) {
      dependents.add(edge.fromNodeId);
    } else {
      dependentIdsByNodeId.set(edge.toNodeId, new Set([edge.fromNodeId]));
    }
  }

  const dependentsByKey = new Map<string, ExportNode[]>();
  for (const [nodeId, dependentIds] of dependentIdsByNodeId) {
    const node = nodesById.get(nodeId);
    if (!node) {
      continue;
    }
    dependentsByKey.set(
      lineageRefKey(node),
      [...dependentIds].flatMap((id) => {
        const dependent = nodesById.get(id);
        return dependent ? [dependent] : [];
      }),
    );
  }
  return dependentsByKey;
}

// Make the names more friendly
function titleCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function dependencySheetName(node: ExportNode): string {
  if (node.resourceType === "model" && node.kind) {
    return MODEL_KIND_SHEET_NAMES[node.kind] ?? `${titleCase(node.kind)} Model`;
  }
  return titleCase(node.resourceType);
}

/**
 * One sheet per root resource type, holding every dependency pair in the
 * workspace to LINEAGE_EXPORT_MAX_DEPTH, followed by the full node and edge sets. Types with no
 * dependents keep an empty sheet so the workbook covers the whole graph.
 */
export async function buildLineageExportSheets({
  nodes,
  edges,
  resourceUrl,
}: LineageExportInput): Promise<LineageExportSheet[]> {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const dependentsByKey = buildDependentsIndex(nodes, edges);
  const sortedNodes = [...nodes].sort(compareLineageNodes);
  const readDependents = (parents: readonly LineageExportRef[]) =>
    parents.map((parent) =>
      (dependentsByKey.get(lineageRefKey(parent)) ?? []).map(
        (dependent): LineageDependent<ExportNode> => ({
          node: dependent,
          hasDependents: dependentsByKey.has(lineageRefKey(dependent)),
        }),
      ),
    );

  const rowsBySheetName = new Map<string, string[][]>();
  for (const node of sortedNodes) {
    const sheetName = dependencySheetName(node);
    let rows = rowsBySheetName.get(sheetName);
    if (!rows) {
      rows = [];
      rowsBySheetName.set(sheetName, rows);
    }

    const dependents = await collectLineageDependents({
      root: node,
      readDependents,
    });
    rows.push(
      ...buildDependencyRows({
        root: { ...node, url: resourceUrl(node) },
        dependents,
        resourceUrl,
      }),
    );
  }

  const dependencySheets = [...rowsBySheetName.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, rows]) => ({
      name,
      rows: [DEPENDENCY_EXPORT_HEADERS, ...rows],
    }));

  const nodeRows = sortedNodes.map((node) => [
    node.resourceId,
    node.resourceType,
    node.name ?? "",
    resourceUrl(node),
    node.exists ? "TRUE" : "FALSE",
  ]);

  const edgeResourceId = (nodeId: string) =>
    nodesById.get(nodeId)?.resourceId ?? "";

  const edgeRows = [...edges]
    .sort(
      (a, b) =>
        a.edgeType.localeCompare(b.edgeType) ||
        edgeResourceId(a.fromNodeId).localeCompare(
          edgeResourceId(b.fromNodeId),
        ) ||
        edgeResourceId(a.toNodeId).localeCompare(edgeResourceId(b.toNodeId)),
    )
    .map((edge) => {
      const from = nodesById.get(edge.fromNodeId);
      const to = nodesById.get(edge.toNodeId);
      return [
        edge.edgeType,
        from?.resourceId ?? "",
        from?.resourceType ?? "",
        from?.name ?? "",
        from ? resourceUrl(from) : "",
        to?.resourceId ?? "",
        to?.resourceType ?? "",
        to?.name ?? "",
        to ? resourceUrl(to) : "",
      ];
    });

  return [
    ...dependencySheets,
    { name: NODES_SHEET_NAME, rows: [NODE_HEADERS, ...nodeRows] },
    { name: EDGES_SHEET_NAME, rows: [EDGE_HEADERS, ...edgeRows] },
  ];
}
